import { useEffect, useMemo, useRef, useState } from "react";
import "./NewsletterGallery.css";
import { useTranslation } from "react-i18next";

// ✅ JSON importé (objet avec { newsletterArticles: [...] })
import rawData from "../data/newsletter.json";

export default function NewsletterGallery({ autoplayMs = 12000 }) {
  const { t } = useTranslation("newsletter");

  // ----- Données -----
  // Normalise: récupère rawData.newsletterArticles (ou tableau vide)
  const articles = useMemo(
    () => (Array.isArray(rawData?.newsletterArticles) ? rawData.newsletterArticles : []),
    []
  );

  // Form newsletter
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Marquee
  const trackRef = useRef(null);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.classList.remove("no-animate");
    track.style.animationDuration = `${autoplayMs}ms`;
  }, [autoplayMs]);

  // Submit newsletter
  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailTrim = email.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      alert(t("alerts.invalidEmail"));
      return;
    }

    try {
      setLoading(true);
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailTrim }),
      });
      if (!res.ok) throw new Error(await res.text());
      setEmail("");
      alert(t("alerts.subscribeSuccess"));
    } catch (err) {
      console.error(err);
      alert(t("alerts.tryLater"));
    } finally {
      setLoading(false);
    }
  };

  // Double la liste pour l’effet “marquee”
  const reel = articles.length ? [...articles, ...articles] : [];

  return (
    <section className="nlz">
      <h2 className="nlz-title">{t("sectionTitle")}</h2>

      <div className="nlz-inner">
        {/* Colonne gauche : texte + formulaire */}
        <div className="nlz-copy">
          <h3>{t("title")}</h3>
          <p>{t("subtitle")}</p>

          <form className="nlz-form" onSubmit={handleSubmit}>
            <div className="nlz-inputwrap">
              <input
                type="email"
                placeholder={t("emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-label={t("emailAria")}
                disabled={loading}
                autoComplete="email"
                inputMode="email"
                name="newsletter-email"
              />
              <button type="submit" disabled={loading}>
                {loading ? t("sending") : t("ctaLabel")}
              </button>
            </div>
          </form>
        </div>

        {/* Colonne droite : galerie d’articles */}
        <div className="nlz-gallery">
          {!articles.length ? (
            <div className="nlz-error">{t("alerts.loadError") || "No articles found."}</div>
          ) : (
            <>
              <div className="nlz-track" ref={trackRef}>
                {reel.map((art, i) => (
                  <a
                    className="nlz-item"
                    key={`${art.id || "art"}-${i}`}
                    href={art.pdf_file}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={art.title}
                    aria-label={`${t("openPdf") || "Open PDF"}: ${art.title}`}
                  >
                    <img src={art.image} alt={art.title} loading="lazy" />
                    {(art.title || art.description) && (
                      <div className="nlz-caption">
                        {art.title && <div className="nlz-caption-title">{art.title}</div>}
                        {art.description && (
                          <div className="nlz-caption-desc">{art.description}</div>
                        )}
                      </div>
                    )}
                  </a>
                ))}
              </div>
              <div className="nlz-glow" aria-hidden />
            </>
          )}
        </div>
      </div>
    </section>
  );
}
